import { useEffect, useMemo, useState } from 'react';
import { Check, ChevronDown, ChevronUp, Lock, MapPin, Plus, Save, Trash2, Users } from 'lucide-react';
import {
  emptyPlaceProgress,
  emptyRegionProgress,
  type Place,
  type PlaceKind,
  type PlaceProgress,
  type Region,
  type RegionProgress,
  type RegionStatus,
} from '../types';
import { statusMeta } from './KoreaMap';
import { Stars } from './Stars';

type Props = {
  region: Region;
  progress?: RegionProgress;
  places: Place[];
  placeProgress: PlaceProgress[];
  busyKey: string | null;
  onSaveRegion: (progress: RegionProgress) => Promise<void>;
  onSavePlace: (placeId: string, progress: PlaceProgress) => Promise<void>;
  onAddPlace: (input: { regionId: string; kind: PlaceKind; name: string; subtitle: string; themes: string[] }) => Promise<void>;
  onDeletePlace: (placeId: string) => Promise<void>;
};

const statuses = Object.entries(statusMeta) as Array<[RegionStatus, { label: string; color: string }]>;

export function RegionPanel(props: Props) {
  const { region, progress, places, placeProgress, busyKey } = props;
  const [draft, setDraft] = useState<RegionProgress>(progress ?? emptyRegionProgress(region.id));
  const [placeTab, setPlaceTab] = useState<PlaceKind>('attraction');
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState('');
  const [addSubtitle, setAddSubtitle] = useState('');

  useEffect(() => setDraft(progress ?? emptyRegionProgress(region.id)), [progress, region.id]);
  const currentPlaces = useMemo(() => places.filter((place) => place.regionId === region.id && place.kind === placeTab), [places, placeTab, region.id]);
  const completed = currentPlaces.filter((place) => placeProgress.find((item) => item.placeId === place.id)?.isChecked).length;

  const addPlace = async () => {
    if (!addName.trim()) return;
    await props.onAddPlace({ regionId: region.id, kind: placeTab, name: addName.trim(), subtitle: addSubtitle.trim(), themes: [] });
    setAddName(''); setAddSubtitle(''); setShowAdd(false);
  };

  return (
    <section className="region-panel">
      <header className="region-heading">
        <div><span className="eyebrow"><MapPin size={13} /> 선택 지역</span><h2>{region.name}</h2><p>{region.description}</p></div>
        <div className="season-pills">{region.recommendedMonths.map((month) => <span key={month}>{month}월</span>)}</div>
      </header>

      <div className="status-picker" aria-label="지역 여행 상태">
        {statuses.map(([status, meta]) => (
          <button type="button" key={status} className={draft.status === status ? 'is-active' : ''} onClick={() => setDraft({ ...draft, status })}>
            <i style={{ background: meta.color }} />{meta.label}
          </button>
        ))}
      </div>

      <div className="region-form-grid">
        <label><span>지역 별점</span><Stars value={draft.rating} onChange={(rating) => setDraft({ ...draft, rating })} /></label>
        <label><span>최근 방문일</span><input type="date" value={draft.lastVisitedOn ?? ''} onChange={(event) => setDraft({ ...draft, lastVisitedOn: event.target.value || null })} /></label>
        <label className="field-wide"><span>한줄 후기 <small>{draft.review.length}/180</small></span><input maxLength={180} placeholder="이 지역을 한 문장으로 기억해 보세요" value={draft.review} onChange={(event) => setDraft({ ...draft, review: event.target.value })} /></label>
        <label className="field-wide"><span><Lock size={13} /> 비공개 메모 <small>{draft.privateNote.length}/2000</small></span><textarea rows={3} maxLength={2000} placeholder="예약 정보, 다시 갈 곳처럼 나만 볼 내용을 적으세요" value={draft.privateNote} onChange={(event) => setDraft({ ...draft, privateNote: event.target.value })} /></label>
        <label className="field-wide"><span><Users size={13} /> 동반자 태그</span><input placeholder="가족, 친구, 혼자 (쉼표로 구분)" value={draft.companionTags.join(', ')} onChange={(event) => setDraft({ ...draft, companionTags: event.target.value.split(',').map((tag) => tag.trim()).filter(Boolean).slice(0, 8) })} /></label>
      </div>
      <div className="region-form-actions">
        <label className="check-row"><input type="checkbox" checked={draft.isNextCandidate} onChange={(event) => setDraft({ ...draft, isNextCandidate: event.target.checked })} /><span>다음 여행 후보로 표시</span></label>
        <label className="visibility-toggle"><span>공개 범위</span><select value={draft.visibility} onChange={(event) => setDraft({ ...draft, visibility: event.target.value as 'private' | 'public' })}><option value="private">나만 보기</option><option value="public">프로필에 공개</option></select></label>
        <button type="button" className="button button-primary" disabled={busyKey === `region:${region.id}`} onClick={() => props.onSaveRegion(draft)}><Save size={16} /> {busyKey === `region:${region.id}` ? '저장 중…' : '지역 기록 저장'}</button>
      </div>

      <div className="places-section">
        <div className="section-title-row"><div><h3>장소 컬렉션</h3><p>기본 5곳에 나만의 장소를 더할 수 있어요.</p></div><div className="mini-progress"><span>{completed}/{currentPlaces.length}</span><i><b style={{ width: `${currentPlaces.length ? completed / currentPlaces.length * 100 : 0}%` }} /></i></div></div>
        <div className="tab-row">
          <button type="button" className={placeTab === 'attraction' ? 'is-active' : ''} onClick={() => setPlaceTab('attraction')}>대표 관광지</button>
          <button type="button" className={placeTab === 'food' ? 'is-active' : ''} onClick={() => setPlaceTab('food')}>맛집·음식문화</button>
          <button type="button" className="add-place-button" onClick={() => setShowAdd((value) => !value)}><Plus size={15} /> 장소 추가</button>
        </div>
        {showAdd && <div className="add-place-form"><input autoFocus placeholder="장소 이름" value={addName} onChange={(event) => setAddName(event.target.value)} /><input placeholder="짧은 설명 (선택)" value={addSubtitle} onChange={(event) => setAddSubtitle(event.target.value)} /><button className="button button-primary" type="button" onClick={addPlace}>추가</button></div>}
        <div className="place-list">
          {currentPlaces.map((place, index) => (
            <PlaceCard
              key={place.id}
              number={index + 1}
              place={place}
              progress={placeProgress.find((item) => item.placeId === place.id) ?? emptyPlaceProgress(place.id)}
              busy={busyKey === `place:${place.id}`}
              onSave={(value) => props.onSavePlace(place.id, value)}
              onDelete={() => props.onDeletePlace(place.id)}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

type PlaceCardProps = {
  number: number;
  place: Place;
  progress: PlaceProgress;
  busy: boolean;
  onSave: (value: PlaceProgress) => Promise<void>;
  onDelete: () => Promise<void>;
};

function PlaceCard({ number, place, progress, busy, onSave, onDelete }: PlaceCardProps) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(progress);
  useEffect(() => setDraft(progress), [progress]);

  const toggleChecked = async () => {
    const next = { ...draft, isChecked: !draft.isChecked };
    setDraft(next);
    await onSave(next);
  };

  return (
    <article className={`place-card ${draft.isChecked ? 'is-complete' : ''}`}>
      <div className="place-card-main">
        <button type="button" className="place-check" aria-label={`${place.name} ${draft.isChecked ? '완료 해제' : '완료 체크'}`} onClick={toggleChecked}>{draft.isChecked ? <Check size={17} /> : number}</button>
        <button type="button" className="place-summary" onClick={() => setOpen((value) => !value)}>
          <span><b>{place.name}</b>{!place.isSeed && <em>내 장소</em>}</span><small>{place.subtitle || '나만의 여행 장소'}</small>
        </button>
        <Stars value={draft.rating} small />
        <button type="button" className="icon-button" aria-label="상세 열기" onClick={() => setOpen((value) => !value)}>{open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}</button>
      </div>
      {open && (
        <div className="place-editor">
          <div className="place-editor-grid">
            <label><span>별점</span><Stars value={draft.rating} onChange={(rating) => setDraft({ ...draft, rating })} /></label>
            <label><span>방문일</span><input type="date" value={draft.visitedOn ?? ''} onChange={(event) => setDraft({ ...draft, visitedOn: event.target.value || null })} /></label>
            <label className="field-wide"><span>한줄 후기</span><input maxLength={180} value={draft.review} onChange={(event) => setDraft({ ...draft, review: event.target.value })} placeholder="기억에 남은 점을 짧게 남겨보세요" /></label>
            <label className="field-wide"><span><Lock size={13} /> 비공개 메모</span><textarea rows={2} maxLength={2000} value={draft.privateNote} onChange={(event) => setDraft({ ...draft, privateNote: event.target.value })} /></label>
            <label><span>동반자</span><input value={draft.companionTags.join(', ')} onChange={(event) => setDraft({ ...draft, companionTags: event.target.value.split(',').map((tag) => tag.trim()).filter(Boolean).slice(0, 8) })} placeholder="친구, 가족" /></label>
            <label><span>공개 범위</span><select value={draft.visibility} onChange={(event) => setDraft({ ...draft, visibility: event.target.value as 'private' | 'public' })}><option value="private">나만 보기</option><option value="public">프로필에 공개</option></select></label>
          </div>
          <div className="place-editor-actions">
            {!place.isSeed && <button type="button" className="button button-danger" onClick={onDelete}><Trash2 size={15} /> 내 장소 삭제</button>}
            <button type="button" className="button button-primary" disabled={busy} onClick={() => onSave(draft)}><Save size={15} /> {busy ? '저장 중…' : '장소 기록 저장'}</button>
          </div>
        </div>
      )}
    </article>
  );
}
